import { useRef, useEffect } from 'react';
import { isChatInProgress } from './chatState';

const API_URL = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
const API_ORIGIN = (() => {
  try {
    return new URL(API_URL).origin;
  } catch {
    return '';
  }
})();
const API_ORIGIN_ALT = API_ORIGIN ? API_ORIGIN.replace('localhost', '127.0.0.1') : '';
const STORAGE_KEY = 'daily-huddle-board';
const LEGACY_STORAGE_KEY = 'daily-huddle-lesson';
const getIframeOrigin = (iframeRef) => {
  const src = iframeRef?.current?.src;
  if (!src) return API_ORIGIN || API_ORIGIN_ALT || '*';
  try {
    return new URL(src, window.location.origin).origin;
  } catch {
    return API_ORIGIN || API_ORIGIN_ALT || '*';
  }
};

// Completely isolated component that only handles iframe reloads
// This component never updates its state, so it won't cause parent re-renders
export default function LessonIframe({ iframeRef, lessonFrameRef, setIsLessonLoading, selectedTemplate = 'template-1' }) {
  const lastVersionRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const lastTemplateRef = useRef(selectedTemplate);
  const selectedTemplateRef = useRef(selectedTemplate);
  const pendingReloadTimeoutRef = useRef(null);
  
  // Log when component mounts
  useEffect(() => {
    console.log('[LessonIframe] helper component mounted');
    console.log('[LessonIframe] iframeRef.current:', iframeRef.current);
    console.log('[LessonIframe] API_URL:', API_URL);
    return () => {
      console.log('[LessonIframe] helper component unmounting');
    };
  }, []);

  // Reset lastModified when template changes
  useEffect(() => {
    if (lastTemplateRef.current !== selectedTemplate) {
      console.log(`Template changed from ${lastTemplateRef.current} to ${selectedTemplate}, resetting lastModified`);
      lastVersionRef.current = null;
      lastTemplateRef.current = selectedTemplate;
      isInitialLoadRef.current = true;
      selectedTemplateRef.current = selectedTemplate;
      if (pendingReloadTimeoutRef.current) {
        clearTimeout(pendingReloadTimeoutRef.current);
        pendingReloadTimeoutRef.current = null;
      }
    }
  }, [selectedTemplate]);

  // Poll for board.html changes and reload iframe when updated
  useEffect(() => {
    let pollCount = 0;
    const checkForUpdates = async () => {
      pollCount++;
      console.log(`[Poll #${pollCount}] Checking for board updates (template: ${selectedTemplate})...`);
      try {
        // Skip polling if offline
        if (typeof navigator !== 'undefined' && navigator && navigator.onLine === false) {
          console.warn('[LessonIframe] Offline, skipping poll');
          return;
        }

        const response = await fetch(`${API_URL}/board/last-modified?template=${encodeURIComponent(selectedTemplate)}`);
        const data = await response.json();
        console.log(`[Poll #${pollCount}] Response:`, data);
        
        if (data.exists && data.last_modified) {
          const currentVersion = data.version || String(data.last_modified);
          if (lastVersionRef.current === null) {
            // First load - just set version token in ref
            lastVersionRef.current = currentVersion;
            console.log(`[Poll #${pollCount}] Initial board version set:`, currentVersion);
          } else if (currentVersion !== lastVersionRef.current) {
            // File was modified
            console.log(`[Poll #${pollCount}] âš ï¸ Board file changed! Old:`, lastVersionRef.current, 'New:', currentVersion);
            // File was modified - but check if chat is in progress first
            const chatInProgress = isChatInProgress();
            console.log(`[Poll #${pollCount}] Chat in progress:`, chatInProgress);
            
            if (chatInProgress) {
              // Chat request is in progress, delay reload to avoid interrupting AI response
              // Will retry on next poll (in 3 seconds)
              console.log(`[Poll #${pollCount}] â¸ï¸ Chat in progress, delaying iframe reload until chat completes...`);
              return;
            }
            
            // File was modified and chat is not in progress - reload iframe only
            // Store the timestamp but delay the actual reload
            const modifiedTime = data.last_modified;
            lastVersionRef.current = currentVersion;
            
            if (iframeRef.current) {
              console.log(`[Poll #${pollCount}] âœ… Board updated, scheduling reload in 2 seconds...`);
              // Show loading indicator when reload starts
              if (setIsLessonLoading) {
                setIsLessonLoading(true);
              }
              
              // Add a delay to ensure any pending chat responses are fully processed
              if (pendingReloadTimeoutRef.current) {
                clearTimeout(pendingReloadTimeoutRef.current);
                pendingReloadTimeoutRef.current = null;
              }
              pendingReloadTimeoutRef.current = setTimeout(() => {
                const activeTemplate = selectedTemplateRef.current;
                if (activeTemplate !== selectedTemplate) {
                  console.log(`[Poll #${pollCount}] Template changed to ${activeTemplate}; skipping stale reload for ${selectedTemplate}`);
                  if (setIsLessonLoading) {
                    setIsLessonLoading(false);
                  }
                  pendingReloadTimeoutRef.current = null;
                  return;
                }
                // Double-check chat is still not in progress before reloading
                if (iframeRef.current && !isChatInProgress()) {
                  // For cross-origin iframes, we must use cache-busting instead of reload()
                  // because contentWindow.location.reload() is blocked by CORS
                  const baseSrc = `${API_URL}/board.html?template=${encodeURIComponent(activeTemplate)}&preview=true`;
                  // Use both modifiedTime and current timestamp for better cache-busting
                  const cacheBuster = `${modifiedTime}_${Date.now()}`;
                  const newSrc = `${baseSrc}&_t=${cacheBuster}`;
                  
                  console.log(`[Poll #${pollCount}] ???? Reloading iframe with cache-busting:`, newSrc);
                  console.log(`[Poll #${pollCount}] Current iframe src:`, iframeRef.current.src);
                  
                  // Always update src to force reload, even if it looks similar
                  iframeRef.current.src = newSrc;
                  
                  // Double-check it was set
                  setTimeout(() => {
                    console.log(`[Poll #${pollCount}] ??? Iframe src after update:`, iframeRef.current?.src);
                  }, 100);
                } else {
                  console.log(`[Poll #${pollCount}] ?????? Reload skipped - chat still in progress`);
                  // Hide loading if we skipped reload
                  if (setIsLessonLoading) {
                    setIsLessonLoading(false);
                  }
                }
                pendingReloadTimeoutRef.current = null;
              }, 2000); // 2 second delay to ensure chat response is fully processed and rendered
            } else {
              console.log(`[Poll #${pollCount}] âš ï¸ iframeRef.current is null!`);
            }
          } else {
            console.log(`[Poll #${pollCount}] No changes detected (timestamp: ${data.last_modified})`);
          }
        } else {
          console.log(`[Poll #${pollCount}] âš ï¸ Board file doesn't exist or no timestamp`);
        }
      } catch (error) {
        console.error(`[Poll #${pollCount}] âŒ Error checking for board updates:`, error);
        // Notify parent about polling errors
        if (setIsLessonLoading) {
          // Don't set loading to false on polling errors, just log them
          // The iframe itself might still be working
        }
      }
    };

    // Check immediately, then every 4 seconds (slightly longer to reduce load)
    console.log('[LessonIframe] Starting board polling...');
    checkForUpdates();
    const interval = setInterval(checkForUpdates, 4000);
    console.log('[LessonIframe] Polling interval set to 4 seconds');

    return () => {
      console.log('[LessonIframe] Stopping board polling...');
      clearInterval(interval);
      if (pendingReloadTimeoutRef.current) {
        clearTimeout(pendingReloadTimeoutRef.current);
        pendingReloadTimeoutRef.current = null;
      }
    };
  }, [iframeRef, selectedTemplate]);

  // Handle initial load for restoring state using postMessage (cross-origin safe)
  useEffect(() => {
    const handleMessage = (event) => {
      // Accept messages from backend origin
      const isBackendOrigin = event.origin === API_ORIGIN || 
                              event.origin === API_ORIGIN_ALT;
      
      if (!isBackendOrigin) {
        return;
      }
      
      if (event.data.type === 'IFRAME_LOADED' && iframeRef.current) {
        // Hide loading indicator when iframe is loaded
        if (setIsLessonLoading) {
          setIsLessonLoading(false);
        }
        
        // Restore state on initial load or after reload
        if (isInitialLoadRef.current) {
          const raw = localStorage.getItem(STORAGE_KEY) || localStorage.getItem(LEGACY_STORAGE_KEY);
          if (raw) {
            try {
              const data = JSON.parse(raw);
              // Send restore message to iframe via postMessage
              iframeRef.current.contentWindow.postMessage({
                type: 'RESTORE_STATE',
                payload: data
              }, getIframeOrigin(iframeRef));
              console.log('State restored to iframe');
            } catch (err) {
              console.error('Restore failed', err);
            }
          }
          isInitialLoadRef.current = false;
        } else {
          // Iframe reloaded after update - just log it
          console.log('Iframe reloaded successfully');
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [iframeRef]);

  return null; // This component doesn't render anything, it just manages the iframe
}
